import * as React from "react";
import { styled, SxProps, Box, Tooltip, Button, Stack, Typography, ClickAwayListener } from "@mui/material";

import {
  GetInFrame,
  GetOutFrame,
  GetPreviewDuration,
  GetPreviewFps,
  GetResetInAndOutFrame,
  SetInFrame,
  SetOutFrame,
  SetPreviewTime,
} from "../../Redux/Reducers/redux-slice-timetool";
import { useSelector } from "react-redux";
import { CreateMarker, JWPMarkerTimecode } from "../../Utils/Timecode/ReactPlayer";
import { ReactPlayerMarker } from "../../Redux/Reducers/redux-slice-react-player";
import { useAppDispatch } from "../../Redux/redux-store";

export type TimetoolPreviewTimelineProps = {
  sx?: SxProps;
  size?: "small" | string;
};

const TimetoolPreviewTimeline: React.FC<TimetoolPreviewTimelineProps> = (props) => {
  const dispatch = useAppDispatch();
  const $timeline = React.useRef<HTMLElement>(undefined);
  const baseBoundingRect = React.useRef({ left: 0, width: 0 });
  const isHandling = React.useRef({ left: false, right: false });
  const inFrame = useSelector(GetInFrame) || 0;
  const outFrame = useSelector(GetOutFrame) || 1;
  const frameRate = useSelector(GetPreviewFps);
  const resetInAndOutFrame = useSelector(GetResetInAndOutFrame);

  const timecodeIn = "";

  const duration = useSelector(GetPreviewDuration);
  const [localFrameMaker, setLocalFrameMaker] = React.useState({
    start: inFrame || 0,
    end: outFrame || 1,
  });

  const [tooltipVisibility, setTooltipVisibility] = React.useState({ left: false, right: false });

  React.useEffect(() => {
    setLocalFrameMaker({
      start: inFrame,
      end: outFrame,
    });
  }, [inFrame, outFrame, resetInAndOutFrame]);

  const pos = React.useMemo(() => {
    const left = (localFrameMaker.start * 100) / duration;
    const right = (localFrameMaker.end * 100) / duration;

    return {
      left,
      right,
    };
  }, [localFrameMaker.start, localFrameMaker.end, duration]);

  const progressBarPos = React.useMemo(() => {
    const left = pos.left < pos.right ? pos.left : pos.right;
    const right = pos.left < pos.right ? pos.right : pos.left;

    return {
      left,
      right,
    };
  }, [pos]);

  const onMouseDown = React.useCallback((handler: string) => {
    if (isHandling.current.left || isHandling.current.right) {
      return;
    }
    isHandling.current = { left: handler === "left", right: handler === "right" };

    setTooltipVisibility({
      left: handler === "left",
      right: handler === "right",
    });
  }, []);

  const onMouseUp = React.useCallback(() => {
    var local = { left: isHandling.current.left, right: isHandling.current.right };
    isHandling.current = { left: false, right: false };

    // Seek video to position
    if (local.left) {
      dispatch(SetInFrame(Math.round(localFrameMaker.start || 0)));
      setTimeout(() => dispatch(SetPreviewTime(localFrameMaker.start / frameRate)), 0);
    } else if (local.right) {
      dispatch(SetOutFrame(Math.round(localFrameMaker.end || 0)));
      setTimeout(() => dispatch(SetPreviewTime(localFrameMaker.end / frameRate)), 0);
    }
  }, [localFrameMaker.start, localFrameMaker.end]);

  const onClickAway = React.useCallback(() => {
    setTooltipVisibility({
      left: false,
      right: false,
    });
    onMouseUp();
  }, [localFrameMaker.start, localFrameMaker.end]);

  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = React.useCallback(
    (e) => {
      if (!isHandling.current.left && !isHandling.current.right) {
        return;
      }

      const pos = Math.max(
        0,
        Math.min(((e.clientX - baseBoundingRect.current.left) / baseBoundingRect.current.width) * 100, 100)
      );
      const timecode = (pos * duration) / 100;

      const _start = isHandling.current.left ? timecode : localFrameMaker.start;
      const _end = isHandling.current.right ? timecode : localFrameMaker.end;
      setLocalFrameMaker({ start: _start, end: _end });
    },
    [props, localFrameMaker.start, localFrameMaker.end]
  );

  const setSizes = React.useCallback(() => {
    if (!$timeline.current) {
      return;
    }
    const _pos = $timeline.current.getBoundingClientRect();
    baseBoundingRect.current.left = _pos.left;
    baseBoundingRect.current.width = $timeline.current.clientWidth;
  }, [$timeline.current]);

  React.useEffect(() => {
    setSizes();
    window.addEventListener("resize", setSizes);
    return () => window.removeEventListener("resize", setSizes);
  }, [setSizes]);

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <StyledTimeline>
        <StyledRail onMouseMove={onMouseMove} ref={$timeline} size={props.size}>
          <Tooltip
            open={tooltipVisibility.left}
            arrow
            placement="bottom"
            title={
              <TooltipTimeline
                marker={CreateMarker(localFrameMaker.start || 0)}
                frameRate={frameRate}
                onClickReduxCall={SetInFrame}
              />
            }
          >
            <StyledHandle
              size={props.size}
              position={pos.left}
              onMouseDown={() => onMouseDown("left")}
              onMouseUp={() => onMouseUp()}
            />
          </Tooltip>
          <Tooltip
            open={tooltipVisibility.right}
            arrow
            placement="bottom"
            title={
              <TooltipTimeline
                marker={CreateMarker(localFrameMaker.end || 1)}
                frameRate={frameRate}
                onClickReduxCall={SetOutFrame}
              />
            }
          >
            <StyledHandle
              size={props.size}
              position={pos.right}
              onMouseDown={() => onMouseDown("right")}
              onMouseUp={() => onMouseUp()}
            />
          </Tooltip>
          <StyledProgress
            size={props.size}
            left={progressBarPos.left}
            right={progressBarPos.right}
            id="tutorial-markers-set-demo"
          />
        </StyledRail>
      </StyledTimeline>
    </ClickAwayListener>
  );
};

const TooltipTimeline: React.FC<{ marker: ReactPlayerMarker; frameRate: number; onClickReduxCall }> = ({
  marker,
  frameRate,
  onClickReduxCall,
}) => {
  const dispatch = useAppDispatch();

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Button
        size="small"
        onClick={() => {
          dispatch(onClickReduxCall(marker.markFrame - 1));
        }}
      >
        <Typography variant="button" lineHeight="inherit" sx={{ width: 18 }}>
          -1
        </Typography>
      </Button>
      <JWPMarkerTimecode frameRate={frameRate} {...marker}></JWPMarkerTimecode>
      <Button
        size="small"
        onClick={() => {
          dispatch(onClickReduxCall(marker.markFrame + 1));
        }}
      >
        <Typography variant="button" lineHeight="inherit" sx={{ width: 18 }}>
          +1
        </Typography>
      </Button>
    </Stack>
  );
};

export default TimetoolPreviewTimeline;

const StyledTimeline = styled(Box)(({ theme }) => ({
  position: "relative",
  paddingRight: theme.spacing(5),
  paddingLeft: theme.spacing(1),
  overflow: "hidden",
  width: "100%",
}));

const StyledProgress = styled(Box, {
  shouldForwardProp: (prop) => prop !== "left" && prop !== "right" && prop !== "size",
})<{
  left: number;
  right: number;
  size?: TimetoolPreviewTimelineProps["size"];
}>(({ theme, left, right, size }) => ({
  position: "absolute",
  top: size === "small" ? 10 : 23,
  height: 4,
  left: `${left}%`,
  width: `${right - left}%`,
  background: theme.palette.primary.text,
}));

const StyledRail = styled(Box, { shouldForwardProp: (prop) => prop !== "size" })<{
  size?: TimetoolPreviewTimelineProps["size"];
}>(({ theme, size }) => ({
  position: "relative",
  paddingTop: size === "small" ? 11 : theme.spacing(3),
  paddingBottom: size === "small" ? 11 : theme.spacing(3),
  "&:before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 2,
    height: 2,
    background: theme.palette.backgroundAlpha.main,
  },
}));

const StyledHandle = styled("button", { shouldForwardProp: (prop) => prop !== "position" && prop !== "size" })<{
  position: number;
  size?: TimetoolPreviewTimelineProps["size"];
}>(({ theme, position, size }) => ({
  position: "absolute",

  transform: "translateX(-50%)",
  top: size === "small" ? 3.5 : 16,
  left: `${position}%`,
  width: 8,
  height: 16,
  padding: 0,
  borderRadius: 2,
  background: theme.palette.primary.main,
  border: "none",
  cursor: "pointer",
  zIndex: 1,
  transition: "outline .3s",
  outline: `3px solid transparent`,

  "&:hover, &:focus": {
    outline: `3px solid ${theme.palette.text.primary}`,
  },
}));
